import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {Moon, Menu, X, Activity} from 'lucide-react'

const navLinks = [
  { label: 'Sleep Calculator', href: '#sleep-calculator' },
  { label: 'Nap Planner', href: '#nap-planner' },
  { label: 'Sleep Debt', href: '#sleep-debt' },
  { label: 'STOP-BANG', href: '#stop-bang' },
  { label: 'ESS Test', href: '#ess' },
  { label: 'CPAP/AHI', href: '#cpap-ahi' },
  { label: 'Jet Lag', href: '#jet-lag' },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleNav = (href: string) => {
    setMobileOpen(false)
    const el = document.querySelector(href)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#080c14]/95 backdrop-blur-xl border-b border-[#1e2d4a]/60 shadow-lg shadow-black/30'
          : 'bg-transparent'
      }`}
      role="banner"
    >
      <nav
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <a
          href="#"
          className="flex items-center gap-2.5 group"
          aria-label="Smart Sleep Calculator Home"
          onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
        >
          <div className="relative w-8 h-8 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-indigo-600/20 group-hover:bg-indigo-600/30 transition-colors" />
            <Moon className="w-4.5 h-4.5 text-indigo-400" size={18} />
          </div>
          <span className="font-bold text-white text-sm sm:text-base tracking-tight">
            Smart<span className="text-gradient-indigo">Sleep</span>
          </span>
          <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500 border border-slate-700/50 rounded-full px-2 py-0.5">
            <Activity size={10} className="text-teal-400" />
            Diagnostics
          </span>
        </a>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-1" role="list">
          {navLinks.map(link => (
            <li key={link.href}>
              <button
                onClick={() => handleNav(link.href)}
                className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/5 transition-all duration-200 font-medium"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        {/* CTA + Mobile toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleNav('#sleep-calculator')}
            className="hidden sm:block btn-primary text-xs py-2 px-4"
            aria-label="Open sleep calculator"
          >
            Calculate Sleep
          </button>
          <button
            className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-[#080c14]/98 backdrop-blur-xl border-b border-[#1e2d4a]/60 overflow-hidden"
          >
            <ul className="px-4 py-4 space-y-1" role="list">
              {navLinks.map(link => (
                <li key={link.href}>
                  <button
                    onClick={() => handleNav(link.href)}
                    className="w-full text-left text-sm text-slate-400 hover:text-white px-4 py-3 rounded-lg hover:bg-white/5 transition-all font-medium"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              <li className="pt-2">
                <button
                  onClick={() => handleNav('#sleep-calculator')}
                  className="w-full btn-primary text-sm py-3"
                >
                  Calculate Sleep Now
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
